/**
 * FilePicker Component
 *
 * A unified file picker that allows users to either:
 * 1. Upload an ephemeral file (temporary, for immediate processing)
 * 2. Select an existing permanent file from storage
 *
 * Returns a fileRef in the format:
 * - `e{hash}` for ephemeral files
 * - `p{hash}` for permanent files
 *
 * Also provides a CustomWidgetRegistration for use in JSON Schema forms.
 */

import {
  html,
  attr,
  Signal,
  OnDispose,
  type Renderable,
  OneOfValue,
  Prop,
  prop,
  Fragment,
} from '@tempots/dom'
import { SegmentedInput } from '@tempots/beatui'
import type { CustomWidgetRegistration } from '@tempots/beatui'
import { UploadEphemeralFile } from './upload-ephemeral-file'
import { Workspace } from '@/state/workspace'
import { Validation } from '@tempots/std'

// ============================================================================
// Types
// ============================================================================

export type FilePickerMode = 'ephemeral' | 'permanent'

export interface FilePickerProps {
  /** Current file reference value (read-only signal) */
  fileRef: Signal<string | undefined>
  /** Callback to set the file reference */
  setFileRef: (ref: string | undefined) => void
  /** Optional label displayed above the control */
  label?: string
  /** Placeholder text when no file is selected */
  placeholder?: string
  /** File type restrictions (e.g., 'image/*', '.pdf') */
  accept?: string
  /** Maximum file size in bytes (for ephemeral uploads) */
  maxFileSize?: number
  /** MIME type filter for permanent file selection */
  mimeTypeFilter?: string
  /** Whether the picker is disabled */
  disabled?: boolean
  /** Called when file selection changes */
  onChange?: (fileRef: string | undefined) => void
  projectId: Signal<string>
}

export interface FilePickerResult {
  /** The file reference (e{hash} or p{hash}) */
  fileRef: string | null
  /** Whether it's an ephemeral or permanent file */
  mode: FilePickerMode | null
  /** The underlying hash without prefix */
  hash: string | null
}

// ============================================================================
// Component
// ============================================================================

/**
 * FilePicker - unified control for ephemeral upload or permanent file selection
 *
 * @example
 * ```typescript
 * const fileRef = prop('')
 *
 * FilePicker({
 *   fileRef,
 *   setFileRef: (ref) => fileRef.set(ref),
 *   label: 'Input File',
 *   accept: 'image/*',
 *   filesService: persistentFiles,
 *   foldersService: folders,
 *   onChange: (ref) => console.log('Selected:', ref),
 * })
 * ```
 */
export function FilePicker(props: FilePickerProps): Renderable {
  const {
    label,
    placeholder = 'Select or upload a file...',
    accept,
    maxFileSize,
    disabled = false,
    setFileRef,
  } = props

  // State
  const mode = prop('ephemeral' as 'ephemeral' | 'permanent' | 'reference')

  console.log('FILE PICKER')
  return html.div(
    attr.class('file-picker space-y-2'),
    // Label
    label
      ? html.label(attr.class('block text-sm font-medium text-gray-700'), label)
      : null,
    SegmentedInput({
      options: {
        ephemeral: 'Upload New',
        permanent: 'Select Stored File',
        reference: 'User Reference File Name',
      },
      value: mode,
      onChange: mode.set,
    }),
    OneOfValue(mode, {
      ephemeral: () => {
        console.log('!!!  ephemeral')
        // return html.div('ephemeral')
        return UploadEphemeralFile({
          accept,
          maxFileSize,
          placeholder,
          disabled,
          onUploadComplete: (result) => {
            console.log('!!!  ephemeral upload complete', result)
            setFileRef(result.fileRef ?? `e${result.hash}`)
          },
          onClear: () => {
            setFileRef(undefined)
          },
        })
      },
      permanent: () => {
        console.log('!!!  permanent')
        return html.div('permanent')
        // return PermanentFileSelector({
        //   // selectedFileHash: permanentHash,
        //   // setFileHash: handlePermanentChange,
        //   placeholder,
        //   projectId,
        //   mimeTypeFilter,
        //   disabled,
        // })
      },
      reference: () => {
        console.log('!!!  reference')
        return html.div('reference')
      },
    })
  )
}

// ============================================================================
// Custom Widget Registration for JSON Schema Forms
// ============================================================================

/**
 * Custom widget for file picker fields in JSON Schema forms.
 *
 * Matches fields with:
 * - name containing "file" (case-insensitive)
 * - format: "file-ref" or "fileRef"
 * - x-widget: "file-picker"
 *
 * @example
 * ```typescript
 * import { FilePickerWidget } from '@/components/ui/file-picker'
 *
 * JSONSchemaForm({
 *   schema: mySchema,
 *   customWidgets: [FilePickerWidget],
 * })
 * ```
 */
export const FilePickerWidget: CustomWidgetRegistration = {
  priority: 90,

  matcher: (ctx) => {
    const schema = ctx.definition as Record<string, unknown> | undefined

    // Match by x-widget annotation
    if (schema?.['x-widget'] === 'file-picker') {
      return true
    }

    return false
  },

  factory: ({ controller, setStatus }) => {
    // Get project services
    // const services = useProjectServices(() => Workspace.projectId.get())
    // const { persistentFiles, folders } = services.get()

    // Create a signal from the controller
    const fileRef = controller.signal.deriveProp() as Prop<string | undefined>

    // Sync controller changes to our signal
    const unsubController = controller.signal.on((value) => {
      fileRef.set(value as string)
    })

    // Extract hints from schema
    const schema = (
      controller as unknown as { schema?: Record<string, unknown> }
    ).schema
    // Support both x-accept (string) and x-accept-types (array of strings)
    const xAcceptTypes = schema?.['x-accept-types'] as string[] | undefined
    const accept =
      xAcceptTypes?.join(',') ?? (schema?.['x-accept'] as string) ?? undefined
    const maxFileSize = (schema?.['x-max-file-size'] as number) ?? undefined
    const mimeTypeFilter =
      (schema?.['x-mime-filter'] as string) ??
      xAcceptTypes?.join(',') ??
      undefined

    console.log('FILE PICKER', fileRef.value)

    return Fragment(
      OnDispose(unsubController),
      FilePicker({
        fileRef,
        setFileRef: (ref) => {
          console.log('!!!  setFileRef', ref)
          fileRef.set(ref)
          controller.change(ref)
          setStatus?.(Validation.valid)
        },
        accept,
        maxFileSize,
        mimeTypeFilter,
        projectId: Workspace.projectId,
      })
    )
  },
}
